import type { FileKind } from '../types';

interface FileIconProps {
  kind: FileKind;
  filename?: string;
  size?: number;
  className?: string;
}

const KIND_GRADIENT: Record<FileKind, string> = {
  image: 'linear-gradient(135deg, #38bdf8, #6366f1)',
  video: 'linear-gradient(135deg, #f43f5e, #d946ef)',
  audio: 'linear-gradient(135deg, #34d399, #14b8a6)',
  document: 'linear-gradient(135deg, #fbbf24, #f97316)',
  archive: 'linear-gradient(135deg, #a78bfa, #6366f1)',
  text: 'linear-gradient(135deg, #94a3b8, #475569)',
  other: 'linear-gradient(135deg, #64748b, #334155)',
};

function extensionFromName(name: string | undefined): string {
  if (!name) return '';
  const idx = name.lastIndexOf('.');
  if (idx <= 0 || idx === name.length - 1) return '';
  return name.slice(idx + 1).toUpperCase().slice(0, 6);
}

function GlyphForKind({ kind, size }: { kind: FileKind; size: number }) {
  const stroke = '#0b0b0f';
  const props = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke,
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (kind) {
    case 'image':
      return (
        <svg {...props}>
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <circle cx="9" cy="10" r="1.6" />
          <path d="m4 18 5-5 4 4 3-2 4 4" />
        </svg>
      );
    case 'video':
      return (
        <svg {...props}>
          <rect x="3" y="5" width="14" height="14" rx="2" />
          <path d="m17 10 5-3v10l-5-3" />
        </svg>
      );
    case 'audio':
      return (
        <svg {...props}>
          <path d="M9 18V7l11-3v11" />
          <circle cx="6" cy="18" r="2.2" />
          <circle cx="17" cy="15" r="2.2" />
        </svg>
      );
    case 'document':
      return (
        <svg {...props}>
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M14 3v5h5" />
          <path d="M9 13h6M9 17h6M9 9h2" />
        </svg>
      );
    case 'archive':
      return (
        <svg {...props}>
          <rect x="3" y="3" width="18" height="4" rx="1" />
          <rect x="3" y="9" width="18" height="12" rx="1" />
          <path d="M10 13h4" />
          <path d="M10 17h4" />
        </svg>
      );
    case 'text':
      return (
        <svg {...props}>
          <rect x="4" y="3" width="16" height="18" rx="2" />
          <path d="M8 8h8M8 12h8M8 16h5" />
        </svg>
      );
    case 'other':
    default:
      return (
        <svg {...props}>
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M14 3v5h5" />
        </svg>
      );
  }
}

export function FileIcon({ kind, filename, size = 28, className = '' }: FileIconProps) {
  const ext = extensionFromName(filename);
  return (
    <div
      role="img"
      aria-label={`${kind} file${ext ? ` (${ext})` : ''}`}
      className={`relative inline-flex items-center justify-center rounded-md ${className}`}
      style={{ background: KIND_GRADIENT[kind] }}
    >
      <GlyphForKind kind={kind} size={Math.round(size * 0.78)} />
      {ext && (
        <span
          className="absolute bottom-1 right-1 rounded-sm px-1 font-mono text-[9px] font-bold leading-tight"
          style={{ background: 'rgba(11,11,15,0.85)', color: '#fde68a' }}
        >
          {ext}
        </span>
      )}
    </div>
  );
}

export function FileIconTile({ kind, filename, className = '' }: { kind: FileKind; filename?: string; className?: string }) {
  return (
    <div className={`grid h-full w-full place-items-center bg-app-surface-2 ${className}`}>
      <FileIcon kind={kind} filename={filename} size={64} className="h-16 w-16" />
    </div>
  );
}
